import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../config/axios';
import { AlertCircle, Activity, Globe, Zap, ArrowUpRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSocket } from '../contexts/SocketContext';
import LoadingSpinner from '../components/UI/LoadingSpinner';

const Dashboard = () => {
  const [monitors, setMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const { socket } = useSocket();
  const [stats, setStats] = useState({
    total: 0,
    online: 0,
    offline: 0,
    avgUptime: 0
  });

  useEffect(() => {
    fetchMonitors();
  }, []);

  useEffect(() => {
    if (!socket) return;

    const handleMonitorUpdate = (data) => {
      setMonitors(prevMonitors => {
        const updatedMonitors = prevMonitors.map(monitor => {
          if (monitor._id === data.monitorId) {
            return {
              ...monitor,
              status: data.status,
              uptime: data.uptime,
              lastChecked: data.timestamp
            };
          }
          return monitor;
        });
        calculateStats(updatedMonitors);
        return updatedMonitors;
      });
    };

    socket.on('monitorUpdate', handleMonitorUpdate);
    return () => socket.off('monitorUpdate', handleMonitorUpdate);
  }, [socket]);

  const fetchMonitors = async () => {
    try {
      const response = await api.get('/api/monitors');
      setMonitors(response.data.monitors);
      calculateStats(response.data.monitors);
    } catch (error) {
      toast.error('Failed to sync infrastructure data');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (monitorsList) => {
    const total = monitorsList.length;
    const online = monitorsList.filter(m => m.status === 'up').length;
    const offline = monitorsList.filter(m => m.status === 'down').length;
    const avgUptime = total > 0
      ? monitorsList.reduce((sum, m) => sum + m.uptime, 0) / total
      : 0;

    setStats({ total, online, offline, avgUptime });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const downMonitors = monitors.filter(m => m.status === 'down');
  const healthyCount = monitors.filter(m => m.status === 'up').length;

  return (
    <div className="animate-fade-in pb-12">
      {/* Dynamic Greeting & Page Header */}
      <div className="mb-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="flex items-center space-x-2 text-brand-600 dark:text-brand-400 font-bold text-xs uppercase tracking-[0.2em] mb-3">
              <div className="h-1 w-8 bg-brand-600 dark:bg-brand-400 rounded-full"></div>
              <span>Command Center</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-surface-900 dark:text-white tracking-tight">
              Infrastructure Overview
            </h1>
          </div>

          <div className="flex items-center space-x-3">
            <div className="px-4 py-2 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800 shadow-premium-sm">
              <p className="text-[10px] font-bold text-surface-400 uppercase tracking-widest leading-none mb-1">Status</p>
              <div className="flex items-center space-x-1.5">
                <div className={`h-2 w-2 rounded-full ${downMonitors.length > 0 ? 'bg-danger animate-pulse' : 'bg-success'}`}></div>
                <span className="text-sm font-bold text-surface-900 dark:text-white">
                  {downMonitors.length > 0 ? 'System Warning' : 'All Systems Operational'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Premium Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        <div className="card-premium p-6 group">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-brand-500/10 rounded-2xl">
              <Globe className="h-6 w-6 text-brand-600 dark:text-brand-400" />
            </div>
            <Link to="/monitors" className="p-2 hover:bg-brand-500/10 rounded-xl transition-all">
              <ArrowUpRight className="h-4 w-4 text-brand-600" />
            </Link>
          </div>
          <div className="space-y-1">
            <p className="text-3xl font-bold text-surface-900 dark:text-white">{stats.total}</p>
            <p className="text-sm font-semibold text-surface-500 uppercase tracking-wider">Total Nodes</p>
          </div>
        </div>

        <div className="card-premium p-6 group border-l-4 border-l-success/30">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-success/10 rounded-2xl">
              <Zap className="h-6 w-6 text-success" />
            </div>
            <span className="text-[10px] font-bold text-success uppercase bg-success/10 px-2 py-1 rounded-full">Synchronized</span>
          </div>
          <div className="space-y-1">
            <p className="text-3xl font-bold text-surface-900 dark:text-white">{stats.online}</p>
            <p className="text-sm font-semibold text-surface-500 uppercase tracking-wider">Online Nodes</p>
          </div>
        </div>

        <div className="card-premium p-6 group border-l-4 border-l-danger/30">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-danger/10 rounded-2xl">
              <AlertCircle className="h-6 w-6 text-danger" />
            </div>
            {stats.offline > 0 && <div className="animate-pulse h-2 w-2 rounded-full bg-danger"></div>}
          </div>
          <div className="space-y-1">
            <p className="text-3xl font-bold text-surface-900 dark:text-white">{stats.offline}</p>
            <p className="text-sm font-semibold text-surface-500 uppercase tracking-wider">Down Now</p>
          </div>
        </div>

        <div className="card-premium p-6 group">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-info/10 rounded-2xl">
              <Activity className="h-6 w-6 text-info" />
            </div>
            <p className="text-[10px] font-bold text-info uppercase">Performance</p>
          </div>
          <div className="space-y-1">
            <p className="text-3xl font-bold text-gradient">{stats.avgUptime.toFixed(1)}%</p>
            <p className="text-sm font-semibold text-surface-500 uppercase tracking-wider">Global Uptime</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        {/* Critical Alerts / Incidents section */}
        <div className="xl:col-span-2 space-y-6">
          <div className="flex items-center justify-between px-2">
            <h3 className="text-xl font-bold text-surface-900 dark:text-white">System Notifications</h3>
            <Link to="/monitors" className="text-xs font-bold text-brand-600 dark:text-brand-400 hover:underline">View All Monitors</Link>
          </div>

          <div className="space-y-4">
            {downMonitors.length > 0 ? (
              downMonitors.map(m => (
                <div key={m._id} className="card-premium !p-5 flex items-center justify-between border-l-4 border-l-danger bg-danger/5">
                  <div className="flex items-center space-x-4">
                    <div className="p-2 bg-danger/10 rounded-xl">
                      <AlertCircle className="h-5 w-5 text-danger" />
                    </div>
                    <div>
                      <h4 className="font-bold text-surface-900 dark:text-white text-sm">{m.name} is Down</h4>
                      <p className="text-xs text-surface-500 font-medium">Node unreachable since {new Date(m.lastChecked).toLocaleTimeString()}</p>
                    </div>
                  </div>
                  <Link to={`/monitor/${m._id}`} className="btn-secondary !py-2 !px-4 !text-xs">Diagnose</Link>
                </div>
              ))
            ) : (
              <div className="card-premium !p-12 text-center bg-gradient-to-br from-success/5 to-transparent border-success/10">
                <div className="p-4 bg-success/10 rounded-2xl inline-block mb-4">
                  <Zap className="h-8 w-8 text-success" />
                </div>
                <h4 className="text-lg font-bold text-surface-900 dark:text-white mb-1">Everything is Stable</h4>
                <p className="text-sm text-surface-500 font-medium">No active incidents detected across your {stats.total} monitoring nodes.</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Health Overview List */}
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-surface-900 dark:text-white px-2 text-center xl:text-left">Node Health</h3>
          <div className="card-premium p-6 space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-surface-500">Operational Nodes</span>
                <span className="text-sm font-bold text-success">{stats.online}</span>
              </div>
              <div className="w-full bg-surface-100 dark:bg-surface-800 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-success h-full transition-all duration-1000"
                  style={{ width: `${(stats.online / stats.total) * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-surface-500">Failed Nodes</span>
                <span className="text-sm font-bold text-danger">{stats.offline}</span>
              </div>
              <div className="w-full bg-surface-100 dark:bg-surface-800 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-danger h-full transition-all duration-1000"
                  style={{ width: `${(stats.offline / stats.total) * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="pt-4 border-t border-surface-100 dark:border-surface-800">
              <Link
                to="/monitors"
                className="w-full btn-secondary !py-3 flex items-center justify-center space-x-2 group"
              >
                <span>Manage Infrastructure</span>
                <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
